/*
 * Copyright 2021 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * External dependencies
 */
import PropTypes from 'prop-types';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from '@web-stories-wp/react';
import { UnitsProvider } from '@web-stories-wp/units';
import { useGlobalIsEitherKeyPressed } from '@web-stories-wp/design-system';

/**
 * Internal dependencies
 */
import { useLayout } from '../layout';
import { useStory } from '../story';

import useCanvasCopyPaste from './useCanvasCopyPaste';
import useEditingElement from './useEditingElement';

import Context from './context';
import { RECT_OBSERVATION_KEY } from './constants';

function CanvasProvider({ children }) {
  const [boundingBoxes, setBoundingBoxes] = useState({});
  const [lastSelectionEvent, setLastSelectionEvent] = useState(null);
  const lastSelectedElementId = useRef(null);
  const [canvasContainer, setCanvasContainer] = useState(null);
  const [pageContainer, setPageContainer] = useState(null);
  const [fullbleedContainer, setFullbleedContainer] = useState(null);
  const [designSpaceGuideline, setDesignSpaceGuideline] = useState(null);
  const [pageAttachmentContainer, setPageAttachmentContainer] = useState(null);
  const [displayLinkGuidelines, setDisplayLinkGuidelines] = useState(false);
  const [showSafeZones, setShowSafeZones] = useState(false);
  const [safeZonePresetId, setSafeZonePresetId] = useState(null);
  const [eyedropperImg, setEyedropperImg] = useState(null);
  const [eyedropperPixelData, setEyedropperPixelData] = useState(null);
  const [isEyedropperActive, setIsEyedropperActive] = useState(null);
  const [eyedropperCallback, setEyedropperCallback] = useState(null);
  const [renamableLayer, setRenamableLayer] = useState(null);
  const [pageCanvasData, setPageCanvasData] = useState(null);
  const [pageCanvasPromise, setPageCanvasPromise] = useState(null);
  const [duplicateDragData, setDuplicateDragData] = useState(null);
  const [activeFilterId, setActiveFilterId] = useState(null);
  const [filterPickerRequest, setFilterPickerRequest] = useState(null);
  const filterPickerRequestId = useRef(0);

  const { areAllKeysPressed, isOnlyKeyPressed } = useGlobalIsEitherKeyPressed([
    'alt',
    'shift',
  ]);
  const shouldDuplicateOnDrag = isOnlyKeyPressed('alt');
  const shouldThrottleDragRotation = isOnlyKeyPressed('shift');
  const shouldDuplicateAndThrottle = areAllKeysPressed;

  // IntersectionObserver tracks clientRects which is what we need here.
  // different from use case of useIntersectionEffect because this is extensible
  // to multiple nodes.
  const clientRectObserver = useMemo(
    () =>
      new window.IntersectionObserver((entries) => {
        for (const entry of entries) {
          if (!(entry.target instanceof HTMLElement)) {
            return;
          }
          if (!entry.target.dataset[RECT_OBSERVATION_KEY]) {
            return;
          }
          const index = entry.target.dataset[RECT_OBSERVATION_KEY];
          setBoundingBoxes((boxes) => ({
            ...boxes,
            [index]: entry.boundingClientRect,
          }));
        }
      }),
    []
  );
  useEffect(() => () => clientRectObserver.disconnect(), [clientRectObserver]);

  const pageSize = useLayout(({ state: { pageWidth, pageHeight } }) => ({
    width: pageWidth,
    height: pageHeight,
  }));

  const {
    nodesById,
    editingElement,
    editingElementState,
    setEditingElementWithState,
    setEditingElementWithoutState,
    clearEditing,
    getNodeForElement,
    setNodeForElement,
  } = useEditingElement();

  const {
    currentPage,
    selectedElementIds,
    toggleElementInSelection,
    setSelectedElementsById,
  } = useStory(
    ({
      state: { currentPage, selectedElementIds },
      actions: { toggleElementInSelection, setSelectedElementsById },
    }) => {
      return {
        currentPage,
        selectedElementIds,
        toggleElementInSelection,
        setSelectedElementsById,
      };
    }
  );

  const handleSelectElement = useCallback(
    (elId, evt, excludeLinkedElements = false) => {
      if (currentPage && currentPage.readonly) {
        return;
      }

      if (editingElement && editingElement !== elId) {
        clearEditing();
      }

      // Skip the focus that immediately follows mouse event.
      // Use the reference to the latest element because the events come in the
      // sequence in the same event loop.
      if (lastSelectedElementId.current === elId && evt.type === 'focus') {
        return;
      }
      lastSelectedElementId.current = elId;

      if (evt.shiftKey) {
        toggleElementInSelection({ elementId: elId });
      } else {
        setSelectedElementsById({
          elementIds: [elId],
          withLinked: !evt.altKey && !excludeLinkedElements,
        });
      }
      evt.currentTarget.focus({ preventScroll: true });
      if (currentPage?.elements[0].id !== elId) {
        evt.stopPropagation();
      }

      if ('mousedown' === evt.type) {
        evt.persist();
        setLastSelectionEvent(evt);

        // Clear this selection event as soon as mouse is released
        // `setTimeout` is currently required to not break functionality.
        evt.target.ownerDocument.addEventListener(
          'mouseup',
          () => window.setTimeout(setLastSelectionEvent, 0, null),
          { once: true, capture: true }
        );
      }
    },
    [
      editingElement,
      currentPage,
      clearEditing,
      toggleElementInSelection,
      setSelectedElementsById,
    ]
  );

  useEffect(() => {
    const doc = typeof document !== 'undefined' ? document : null;
    if (!doc) {
      return undefined;
    }
    const clearLastSelection = () => setLastSelectionEvent(null);
    const win = doc.defaultView;

    doc.addEventListener('pointerup', clearLastSelection, true);
    doc.addEventListener('pointercancel', clearLastSelection, true);
    win?.addEventListener('blur', clearLastSelection, true);

    return () => {
      doc.removeEventListener('pointerup', clearLastSelection, true);
      doc.removeEventListener('pointercancel', clearLastSelection, true);
      win?.removeEventListener('blur', clearLastSelection, true);
    };
  }, [setLastSelectionEvent]);

  // Reset editing mode when selection changes.
  useEffect(() => {
    if (
      editingElement &&
      (selectedElementIds.length !== 1 ||
        selectedElementIds[0] !== editingElement)
    ) {
      clearEditing();
    }
    if (
      lastSelectedElementId.current &&
      !selectedElementIds.includes(lastSelectedElementId.current)
    ) {
      lastSelectedElementId.current = null;
    }
  }, [editingElement, selectedElementIds, clearEditing]);

  useCanvasCopyPaste();

  const toggleSafeZones = useCallback(
    () => setShowSafeZones((current) => !current),
    []
  );

  const requestFilterPicker = useCallback((filterId) => {
    if (!filterId) {
      return;
    }

    filterPickerRequestId.current += 1;
    setFilterPickerRequest({
      filterId,
      requestId: filterPickerRequestId.current,
    });
  }, []);

  const clearFilterPickerRequest = useCallback(
    () => setFilterPickerRequest(null),
    []
  );

  const state = useMemo(
    () => ({
      state: {
        pageContainer,
        canvasContainer,
        fullbleedContainer,
        nodesById,
        editingElement,
        editingElementState,
        isEditing: Boolean(editingElement),
        lastSelectionEvent,
        displayLinkGuidelines,
        showSafeZones,
        safeZonePresetId,
        pageAttachmentContainer,
        designSpaceGuideline,
        isEyedropperActive,
        duplicateDragData,
        eyedropperCallback,
        eyedropperImg,
        eyedropperPixelData,
        boundingBoxes,
        clientRectObserver,
        pageCanvasData,
        pageCanvasPromise,
        renamableLayer,
        activeFilterId,
        filterPickerRequest,
        shouldDuplicateOnDrag,
        shouldThrottleDragRotation,
        shouldDuplicateAndThrottle,
      },
      actions: {
        setPageContainer,
        setFullbleedContainer,
        getNodeForElement,
        setNodeForElement,
        setEditingElement: setEditingElementWithoutState,
        setEditingElementWithState,
        clearEditing,
        handleSelectElement,
        setDisplayLinkGuidelines,
        toggleSafeZones,
        setSafeZonePresetId,
        setPageAttachmentContainer,
        setCanvasContainer,
        setDesignSpaceGuideline,
        setIsEyedropperActive,
        setEyedropperCallback,
        setEyedropperImg,
        setEyedropperPixelData,
        setDuplicateDragData,
        setPageCanvasData,
        setPageCanvasPromise,
        setRenamableLayer,
        setActiveFilterId,
        requestFilterPicker,
        clearFilterPickerRequest,
      },
    }),
    [
      pageContainer,
      fullbleedContainer,
      nodesById,
      editingElement,
      editingElementState,
      lastSelectionEvent,
      setPageContainer,
      setFullbleedContainer,
      getNodeForElement,
      setNodeForElement,
      setEditingElementWithoutState,
      setEditingElementWithState,
      clearEditing,
      handleSelectElement,
      displayLinkGuidelines,
      setDisplayLinkGuidelines,
      showSafeZones,
      safeZonePresetId,
      toggleSafeZones,
      setSafeZonePresetId,
      pageAttachmentContainer,
      setPageAttachmentContainer,
      canvasContainer,
      setCanvasContainer,
      designSpaceGuideline,
      setDesignSpaceGuideline,
      isEyedropperActive,
      setIsEyedropperActive,
      duplicateDragData,
      setDuplicateDragData,
      eyedropperCallback,
      setEyedropperCallback,
      eyedropperImg,
      setEyedropperImg,
      eyedropperPixelData,
      setEyedropperPixelData,
      boundingBoxes,
      clientRectObserver,
      pageCanvasData,
      pageCanvasPromise,
      setPageCanvasPromise,
      renamableLayer,
      setRenamableLayer,
      activeFilterId,
      setActiveFilterId,
      filterPickerRequest,
      requestFilterPicker,
      clearFilterPickerRequest,
      shouldDuplicateOnDrag,
      shouldThrottleDragRotation,
      shouldDuplicateAndThrottle,
    ]
  );
  return (
    <Context.Provider value={state}>
      <UnitsProvider pageSize={pageSize}>{children}</UnitsProvider>
    </Context.Provider>
  );
}

CanvasProvider.propTypes = {
  children: PropTypes.node,
};

export default CanvasProvider;
